import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import type { DataTablePaginationProps } from '~/components/common/data-table/data-table.types';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '~/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { cn } from '~/utils/cn';

/**
 * Returns an ordered list of page numbers to render, with `null` representing
 * an ellipsis gap. Always includes the first and last page.
 *
 * Example outputs:
 *   (1, 9)  → [1, 2, null, 9]
 *   (5, 9)  → [1, null, 4, 5, 6, null, 9]
 *   (8, 9)  → [1, null, 8, 9]
 *   (1, 5)  → [1, 2, 3, 4, 5]  (no ellipsis when total ≤ 7)
 */
function getPageNumbers(current: number, total: number): (number | null)[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  const hasLeftGap = left > 2;
  const hasRightGap = right < total - 1;

  const pages: (number | null)[] = [1];
  if (hasLeftGap) pages.push(null);
  for (let p = left; p <= right; p++) pages.push(p);
  if (hasRightGap) pages.push(null);
  pages.push(total);
  return pages;
}

/**
 * Pagination footer bar built on shadcn's Pagination primitives.
 *
 * Layout: [ Items per page selector  Showing X–Y of Z ]  [ Previous  1  2  …  Next ]
 */
export function DataTablePagination({
  page,
  totalPages,
  totalRows,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  labels,
  hidePageNumbers = false,
  hidePageSizeSelector = false,
  tableItemType,
  className,
  testId = 'data-table',
  extra,
}: DataTablePaginationProps) {
  const { t } = useTranslation('common');
  // The shared i18next instance, not the hook's: a consumer's test may stub the hook with `t` alone.
  const formatNumber = (n: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language).format(n);
  // Skip computing the numbered-page list when it isn't rendered — keeps the
  // cursor-pagination call site (which doesn't know its real `totalPages`)
  // from leaking confusing intermediate page numbers into the DOM.
  const pageNumbers = hidePageNumbers ? [] : getPageNumbers(page, totalPages);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRows);

  // A consumer's own label wins; otherwise the shared one. It also names the page-size select,
  // which shows only a number and is not a <label>'s control.
  const rowsPerPageLabel =
    labels?.rowsPerPage ?? t('dataTable.rowsPerPage', { defaultValue: 'Items per page' });
  // The shared copy when no override is provided, with the numbers grouped for the UI language.
  // The English fallbacks are built here, already filled in, for an app without `common` loaded.
  const range = {
    start: formatNumber(start),
    end: formatNumber(end),
    total: formatNumber(totalRows),
  };
  const englishRange = `Showing ${range.start}–${range.end} of ${range.total}`;
  const showingLabel = labels?.showing
    ? labels.showing({ start, end, total: totalRows })
    : tableItemType
      ? t('dataTable.showingItems', {
          ...range,
          itemType: tableItemType,
          defaultValue: `${englishRange} ${tableItemType}`,
        })
      : t('dataTable.showing', { ...range, defaultValue: englishRange });

  return (
    <div
      className={cn('flex items-center justify-between gap-4', className)}
      data-testid={`${testId}-pagination`}
    >
      {/* Left group: items-per-page selector + "Showing X–Y of Z" (Figma order). */}
      <div className="flex min-w-0 flex-wrap items-center gap-4">
        {/* Items per page selector — suppressed when the consumer's data
            source has a fixed page size (e.g. cursor pagination). */}
        {!hidePageSizeSelector && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground shrink-0 text-sm">{rowsPerPageLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                onPageSizeChange(Number(v));
              }}
            >
              <SelectTrigger
                className="h-9 w-17.5"
                aria-label={rowsPerPageLabel}
                data-testid={`${testId}-page-size-select`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-testid={`${testId}-page-size-select-content`}>
                {pageSizeOptions.map((size) => (
                  <SelectItem
                    key={size}
                    value={String(size)}
                    data-testid={`${testId}-page-size-option-${String(size)}`}
                  >
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <p
          role="status"
          aria-live="polite"
          className="text-muted-foreground shrink-0 text-sm"
          data-testid={`${testId}-pagination-summary`}
        >
          {showingLabel}
        </p>
        {extra}
      </div>

      {/* Right group: page navigation */}
      <div className="flex items-center gap-3">
        <Pagination className="w-auto">
          <PaginationContent>
            {/* Prev */}
            <PaginationItem>
              <PaginationPrevious
                text={t('dataTable.previous', { defaultValue: 'Previous' })}
                data-testid={`${testId}-prev-button`}
                aria-disabled={page <= 1}
                className={cn(
                  'h-9 cursor-pointer border',
                  page <= 1 && 'bg-muted cursor-not-allowed border-0 opacity-50',
                )}
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) onPageChange(page - 1);
                }}
              />
            </PaginationItem>

            {/* Page numbers — suppressed when `hidePageNumbers` is on so the
                pager degrades to a pure Prev / Next control (used by the
                cursor-paginated product-overview table). */}
            {!hidePageNumbers &&
              pageNumbers.map((p, i) =>
                p === null ? (
                  <PaginationItem key={`ellipsis-${String(i)}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === page}
                      aria-label={t('dataTable.page', {
                        page: p,
                        defaultValue: `Page ${String(p)}`,
                      })}
                      data-testid={`${testId}-page-${String(p)}`}
                      className={cn(
                        'mx-1 h-9 cursor-pointer rounded-md border',
                        p === page &&
                          'bg-primary hover:bg-primary/90 border-primary text-white hover:text-white',
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        onPageChange(p);
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}

            {/* Next */}
            <PaginationItem>
              <PaginationNext
                text={t('dataTable.next', { defaultValue: 'Next' })}
                aria-disabled={page >= totalPages}
                data-testid={`${testId}-next-button`}
                className={cn(
                  'h-9 cursor-pointer border',
                  page >= totalPages && 'bg-muted cursor-not-allowed border-0 opacity-50',
                )}
                onClick={(e) => {
                  e.preventDefault();
                  if (page < totalPages) onPageChange(page + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
}
